import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GiHamburgerMenu } from "react-icons/gi";
import { IoIosArrowDown } from "react-icons/io";
import { motion, AnimatePresence } from "framer-motion";
import { FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import { CiLocationOn } from "react-icons/ci";
import logo from "../../Assets/Logos/m-logo.svg";
import logoText from "../../Assets/Logos/logo-text.svg";

const BottomNavbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  const menuItems = [
    { title: "Home", path: "/" },
    { title: "About Us", path: "/about" },
    { title: "Products", path: "/our-products" },
    { title: "Services", path: "/our-services" },
    { title: "Blogs", path: "/blogs" },
    { title: "Contact Us", path: "/contact" },
    { title: "Pricing", path: "/plan-details" },
    { title: "Review", path: "/review" },
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
    setActiveDropdown(null);
    window.scrollTo(0, 0);
  };

  const isActive = (path) => {
    if (!path) return false;
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Top header */}
      <div className="w-full bg-[#001A2C] text-white hidden md:flex">
        <div className="container px-4 lg:px-10 py-2 flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2 hover:text-blue-200">
              <CiLocationOn className="text-md" />
              <a
                href="location:Natapada, Jajpur, Odisah, 755015"
                className="text-sm"
              >
                Near Reliance Fresh Signature, Satya Vihar, Rasulgarh,
                Bhubaneswar, Odisha 751010
              </a>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2 hover:text-blue-200">
              <FaEnvelope className="text-sm" />
              <a href="mailto:info@afsa.org.in" className="text-sm">
                manikainfo@gmail.com
              </a>
            </div>
            <div className="flex items-center space-x-2 hover:text-blue-200">
              <FaPhoneAlt className="text-sm" />
              <a href="tel:+918455957004" className="text-sm">
                7017774127, 7656934714
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* navbar  */}
      <div className="w-full z-50">
        {/* Desktop Navbar */}
        <div className="hidden xl:flex items-center justify-between bg-[#FFFFFF] px-10 shadow-md">
          {/* Left - Logo */}
          <div
            className="flex justify-center items-center gap-2 cursor-pointer"
            onClick={() => {
              navigate("/");
            }}
          >
            <div>
              <img src={logo} alt="Manika Automotive logo" />
            </div>
            <div>
              <img src={logoText} alt="Manika Automotive logo" />
            </div>
          </div>

          {/* Center - Menu */}
          <div className="flex gap-6 items-center text-base text-[#444444]">
            {menuItems.map((item, index) => (
              <div
                key={index}
                className="relative hover:text-[#030B25] hover:font-medium"
              >
                {item.options ? (
                  <div
                    className="flex items-center gap-1 cursor-pointer"
                    onClick={() =>
                      setActiveDropdown(activeDropdown === index ? null : index)
                    }
                  >
                    {item.title} <IoIosArrowDown size={16} />
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavigate(item.path)}
                    className={`${
                      isActive(item.path) ? "text-[#030B25] font-medium" : ""
                    }`}
                  >
                    {item.title}
                  </button>
                )}
                {item.options && activeDropdown === index && (
                  <div className="absolute bg-white text-primaryTextColor shadow-lg mt-2 rounded-md w-40 overflow-hidden z-50">
                    {item.options.map((opt, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleNavigate(opt.path)}
                        className={`px-4 py-2 cursor-pointer ${
                          isActive(opt.path)
                            ? "bg-primaryFadedColor text-[#030B25] font-medium"
                            : "text-[#030B25] hover:bg-primaryFadedColor hover:font-medium"
                        }`}
                      >
                        {opt.title}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Button */}
          <div>
            <button
              onClick={() => navigate("/contact")}
              className="bg-[#E8471B] font-montserrat font-medium text-lg leading-6 tracking-[0.5px] align-middle capitalize text-white px-8 py-4 rounded-md"
            >
              Book a Service
            </button>
          </div>
        </div>

        {/* Mobile Navbar */}
        <div className="xl:hidden flex justify-between items-center px-6 py-4 bg-white shadow-md">
          <div className="h-12 sm:h-16 md:h-auto">
            <img src={logo} alt="logo" className="text-xs h-full " />
          </div>
          <GiHamburgerMenu
            size={28}
            className="text-primaryColor cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          />
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              ref={menuRef}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3 }}
              className="fixed top-0 right-0 w-3/4 h-full bg-white shadow-lg z-50 p-6 flex flex-col gap-6"
            >
              <div className="flex justify-between items-center mb-4">
                <div className="text-lg font-bold text-primaryColor">Menu</div>
                <button
                  className="bg-gray-300 text-gray-800 px-3 py-1 rounded-md"
                  onClick={() => setMenuOpen(false)}
                >
                  Close
                </button>
              </div>
              {menuItems.map((item, index) => (
                <div key={index}>
                  {item.options ? (
                    <div>
                      <div
                        className="flex items-center justify-between text-primaryTextColor font-medium cursor-pointer"
                        onClick={() =>
                          setActiveDropdown(
                            activeDropdown === index ? null : index
                          )
                        }
                      >
                        {item.title}
                        <IoIosArrowDown />
                      </div>
                      {activeDropdown === index && (
                        <div className="ml-4 mt-2 space-y-2">
                          {item.options.map((opt, idx) => (
                            <div
                              key={idx}
                              className={`${
                                isActive(opt.path)
                                  ? "text-[#0070A8]"
                                  : "text-secondaryTextColor"
                              } text-sm hover:text-primaryColor cursor-pointer`}
                              onClick={() => handleNavigate(opt.path)}
                            >
                              {opt.title}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className={`${
                        isActive(item.path)
                          ? "text-[#0070A8]"
                          : "text-primaryTextColor"
                      } font-medium cursor-pointer`}
                      onClick={() => handleNavigate(item.path)}
                    >
                      {item.title}
                    </div>
                  )}
                </div>
              ))}
              <button
                onClick={() => handleNavigate("/contact")}
                className="mt-auto bg-[#E8471B] text-white px-5 py-2 rounded-md w-full"
              >
                Book a Service
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default BottomNavbar;
